import type { CursorPage } from "../types";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly traceId?: string;

  constructor(status: number, title: string, detail: string, traceId?: string) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.title = title;
    if (traceId) this.traceId = traceId;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  csrfToken?: string | null;
};

function buildHeaders(options: RequestOptions): Headers {
  const headers = new Headers(options.headers);
  headers.set("accept", "application/json");
  if (options.body !== undefined) headers.set("content-type", "application/json");
  if (options.csrfToken) headers.set("x-csrf-token", options.csrfToken);
  return headers;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, csrfToken: _csrfToken, ...nativeOptions } = options;
  const requestInit: RequestInit = {
    ...nativeOptions,
    headers: buildHeaders(options),
    credentials: "include",
  };
  if (body !== undefined) requestInit.body = JSON.stringify(body);
  const response = await fetch(`${API_BASE}${path}`, requestInit);

  if (response.status === 401) window.dispatchEvent(new CustomEvent("faro:unauthorized"));

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("json") ? await response.json() as unknown : null;

  if (!response.ok) {
    const problem = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
    throw new ApiError(
      response.status,
      String(problem.title ?? "No se pudo completar la solicitud"),
      String(problem.detail ?? "El servidor no pudo procesar la operación. Intentá nuevamente."),
      typeof problem.traceId === "string" ? problem.traceId : undefined,
    );
  }

  if (response.status === 204) return undefined as T;
  return payload as T;
}

export function queryString(params: Record<string, string | number | boolean | null | undefined>): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) query.set(key, String(value));
  });
  const value = query.toString();
  return value ? `?${value}` : "";
}

export function emptyPage<T>(): CursorPage<T> {
  return { items: [], nextCursor: null };
}
